import { useEffect, useRef, useState } from 'react';
import {
  calcularIct,
  centimetros,
  formatarCm,
  formatarIct,
  ICT_REFERENCIA,
  temCalibracao,
  type Calibracao,
  type Compasso,
  type MedidaDaImagem,
} from '../../../compartilhado/medidas.ts';
import { Botao } from './forma.tsx';
import './regua.css';

/* Régua do índice cardiotorácico, sobre a chapa da tela de laudo.
 *
 * O médico marca duas larguras -- a do coração e a interna do tórax -- e o
 * sistema divide uma pela outra. Como é razão entre duas medidas da MESMA
 * imagem, não há calibração em lugar nenhum: o número é o mesmo com a chapa
 * grande, pequena ou ampliada, e sai certo também nas chapas antigas, de que
 * não se conhece o tamanho do pixel.
 *
 * Cada medida é um compasso HORIZONTAL, e não uma reta livre: o diâmetro
 * transverso é horizontal por definição, e travar a horizontal tira de quem
 * mede a chance de inclinar a linha sem perceber e levar a medida embora.
 *
 * O centímetro aparece ao lado, quando a chapa sabe quanto vale um pixel. É
 * número de apoio, nunca o que decide: numa radiografia de tórax o centímetro
 * é o tamanho NA CHAPA, e a ampliação do coração não tem como ser corrigida
 * sem a distância foco-filme, que estes arquivos não registram.
 */

type Qual = 'cardiaco' | 'toracico';

const PASSOS: Record<Qual, { nome: string; dica: string }> = {
  cardiaco: {
    nome: 'coração',
    dica: 'Clique na borda direita e depois na borda esquerda do coração.',
  },
  toracico: {
    nome: 'tórax',
    dica: 'Clique nas duas paredes internas do tórax, na altura do diafragma.',
  },
};

const ZOOMS = [1, 1.5, 2, 3, 4] as const;

/** Menor largura que vira medida, em fração da imagem. Abaixo disto é o
 *  segundo clique caindo quase sobre o primeiro -- quase sempre um toque
 *  duplo sem intenção, e nunca um diâmetro. */
const LARGURA_MINIMA = 0.01;

/** Passo da seta do teclado. Ajuste fino depois de marcar com o dedo ou com o
 *  mouse, que é onde o último milímetro se resolve. */
const PASSO_TECLADO = 0.002;

const limitar = (v: number) => Math.min(1, Math.max(0, v));

interface Props {
  /** Imagem de visualização (não a de laudo: esta é a que já está na tela). */
  src: string;
  /** Endereço da chapa em tamanho cheio, para o link do painel. */
  cheia: string;
  descricao: string;
  medida: MedidaDaImagem;
  aoMudar: (m: MedidaDaImagem) => void;
  /** Tamanho do pixel desta chapa. Sem ela, a régua só fala em razão. */
  calibracao: Calibracao;
  /** Falso para a recepção, para o médico solicitante e para laudo assinado. */
  podeMedir: boolean;
  /** Escreve o índice no laudo. Recebe o valor, e não a frase: quem sabe como
   *  a linha é escrita é o laudo, não a régua. */
  aoEscreverNoLaudo?: (ict: number) => void;
  /** Tira do laudo a linha do índice. */
  aoTirarDoLaudo?: () => void;
  /** O índice desta chapa já está escrito no laudo? Decide como a opção de
   *  escrever nasce ao reabrir o exame. */
  jaNoLaudo: boolean;
  aoFalhar: () => void;
}

export function ReguaIct({
  src,
  cheia,
  descricao,
  medida,
  aoMudar,
  calibracao,
  podeMedir,
  aoEscreverNoLaudo,
  aoTirarDoLaudo,
  jaNoLaudo,
  aoFalhar,
}: Props) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [caixa, setCaixa] = useState({ w: 0, h: 0 });
  const [nivelZoom, setNivelZoom] = useState(0);

  const [marcando, setMarcando] = useState<Qual | null>(null);
  const [parcial, setParcial] = useState<{ x1: number; y: number } | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [arrasto, setArrasto] = useState<{ qual: Qual; ponta: 'x1' | 'x2' | 'linha' } | null>(null);

  /* Escrever o índice no laudo é escolha do médico, e não consequência de
     medir: medir também serve para só conferir. Nasce marcada quando o índice
     desta chapa já está no laudo -- é o que faz reabrir o exame não mentir
     sobre o que está escrito lá. */
  const [escrevendo, setEscrevendo] = useState(jaNoLaudo);
  useEffect(() => {
    if (jaNoLaudo) setEscrevendo(true);
  }, [jaNoLaudo]);

  /* O arrasto lê a medida por referência, e não pela dependência do efeito:
     cada pixel de movimento muda `medida`, e com ela na dependência o efeito
     se reinscreveria umas sessenta vezes por segundo durante o arrasto. */
  const medidaRef = useRef(medida);
  medidaRef.current = medida;

  const ict = calcularIct(medida);
  const zoom = ZOOMS[nivelZoom] ?? 1;
  const temCm = temCalibracao(calibracao);

  /* Enquanto a opção está marcada, o laudo acompanha a marca: arrastou a
     ponta, o número lá dentro muda junto. A alternativa seria o médico
     arrastar, esquecer de reescrever, e assinar um laudo com o índice de
     antes do ajuste. */
  useEffect(() => {
    if (!escrevendo || ict === null) return;
    aoEscreverNoLaudo?.(ict);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escrevendo, ict]);

  /* O desenho vive em pixels da tela, e não em fração: alça e traço têm de ter
     o mesmo tamanho com a chapa ampliada ou não. Daí medir a caixa da imagem. */
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const medir = () => setCaixa({ w: el.clientWidth, h: el.clientHeight });
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  // Esc abandona a marcação pela metade, sem apagar o que já estava medido.
  useEffect(() => {
    if (!marcando) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMarcando(null);
        setParcial(null);
        setCursor(null);
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [marcando]);

  /* Arrasto de alça. Os ouvintes ficam na janela, e não no SVG: o dedo ou o
     ponteiro sai de cima da marca no meio do movimento, e se o ouvinte fosse
     do elemento a alça ficaria grudada no ponteiro depois de soltar. */
  useEffect(() => {
    if (!arrasto) return;

    const mover = (ev: PointerEvent) => {
      const p = fracaoDoEvento(ev);
      if (!p) return;
      const atual = medidaRef.current[arrasto.qual];
      if (!atual) return;
      const novo: Compasso =
        arrasto.ponta === 'linha' ? { ...atual, y: p.y } : { ...atual, [arrasto.ponta]: p.x };
      aoMudar({ ...medidaRef.current, [arrasto.qual]: novo });
    };
    const soltar = () => setArrasto(null);

    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrasto]);

  /** Ponto do evento em fração da imagem. Lê a caixa na hora, então já vem
   *  certo com a chapa ampliada e rolada. */
  function fracaoDoEvento(ev: { clientX: number; clientY: number }) {
    const el = imgRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return { x: limitar((ev.clientX - r.left) / r.width), y: limitar((ev.clientY - r.top) / r.height) };
  }

  function aoApontar(ev: React.PointerEvent) {
    if (!marcando) return;
    const p = fracaoDoEvento(ev);
    if (!p) return;

    if (!parcial) {
      setParcial({ x1: p.x, y: p.y });
      return;
    }
    if (Math.abs(p.x - parcial.x1) < LARGURA_MINIMA) return;

    aoMudar({ ...medida, [marcando]: { y: parcial.y, x1: parcial.x1, x2: p.x } });
    setParcial(null);
    setCursor(null);
    /* Encadeia: quem acabou de medir o coração está medindo o índice, e o
       índice são as duas larguras. Pedir a segunda é o passo seguinte óbvio --
       mas só quando ela ainda não existe, senão refazer uma marca sozinha
       jogaria o médico de volta na outra. */
    setMarcando(marcando === 'cardiaco' && !medida.toracico ? 'toracico' : null);
  }

  function comecar(qual: Qual) {
    setMarcando(qual);
    setParcial(null);
    setCursor(null);
  }

  function apagarTudo() {
    setMarcando(null);
    setParcial(null);
    aoMudar({ cardiaco: null, toracico: null });
    /* Apagar a marca tira o número do laudo junto: deixar escrito um índice
       sem a marca que o produziu é exatamente o que a migração 029 existe
       para evitar. */
    if (escrevendo) {
      setEscrevendo(false);
      aoTirarDoLaudo?.();
    }
  }

  function alternarEscrita(marcado: boolean) {
    setEscrevendo(marcado);
    if (marcado) {
      if (ict !== null) aoEscreverNoLaudo?.(ict);
    } else {
      aoTirarDoLaudo?.();
    }
  }

  const marcandoAgora = marcando !== null;
  const temAlguma = !!(medida.cardiaco || medida.toracico);
  const cmCardiaco = temCm ? centimetros(medida.cardiaco, calibracao) : null;
  const cmToracico = temCm ? centimetros(medida.toracico, calibracao) : null;

  const controlesDeZoom = (
    <div className="regua__zoom">
      <Botao
        variante="silencioso"
        tamanho="sm"
        aria-label="Diminuir a chapa"
        disabled={nivelZoom === 0}
        onClick={() => setNivelZoom((n) => Math.max(0, n - 1))}
      >
        −
      </Botao>
      <span className="regua__zoom-valor">{Math.round(zoom * 100)}%</span>
      <Botao
        variante="silencioso"
        tamanho="sm"
        aria-label="Ampliar a chapa"
        disabled={nivelZoom === ZOOMS.length - 1}
        onClick={() => setNivelZoom((n) => Math.min(ZOOMS.length - 1, n + 1))}
      >
        +
      </Botao>
    </div>
  );

  /* O mesmo painel vai em cima e embaixo da chapa. A chapa é alta -- uma de
     tórax passa de mil pixels na tela --, e um controle que só existe num dos
     extremos obriga a rolar a página inteira para ligar a régua e rolar de
     volta para ver a imagem. `principal` diz qual dos dois anuncia a dica em
     voz alta: dois avisos iguais seriam lidos duas vezes. */
  const painel = (lugar: 'topo' | 'rodape') => {
    const principal = lugar === 'topo';
    return (
      <div className="regua__painel" data-lugar={lugar}>
        {!podeMedir ? (
          <div className="regua__acoes">
            {ict !== null && (
              <p className="regua__ict">
                <span className="rotulo">Índice cardiotorácico</span>
                <strong>{formatarIct(ict)}</strong>
              </p>
            )}
            <a className="regua__cheia" href={cheia} target="_blank" rel="noreferrer">
              Abrir em tamanho cheio
            </a>
          </div>
        ) : (
          <>
            <div className="regua__acoes">
              {marcandoAgora ? (
                <>
                  <p
                    className="regua__dica"
                    role={principal ? 'status' : undefined}
                    aria-hidden={principal ? undefined : true}
                  >
                    <strong>{PASSOS[marcando].nome}:</strong> {PASSOS[marcando].dica}
                    {parcial && ' Falta a segunda ponta.'}
                  </p>
                  <Botao
                    variante="silencioso"
                    tamanho="sm"
                    onClick={() => {
                      setMarcando(null);
                      setParcial(null);
                      setCursor(null);
                    }}
                  >
                    Cancelar
                  </Botao>
                </>
              ) : !temAlguma ? (
                <>
                  <Botao variante="neutro" tamanho="sm" onClick={() => comecar('cardiaco')}>
                    Medir índice cardiotorácico
                  </Botao>
                  <a className="regua__cheia" href={cheia} target="_blank" rel="noreferrer">
                    Abrir em tamanho cheio
                  </a>
                </>
              ) : (
                <>
                  <Botao variante="silencioso" tamanho="sm" onClick={() => comecar('cardiaco')}>
                    {medida.cardiaco ? 'Refazer o coração' : 'Medir o coração'}
                  </Botao>
                  <Botao variante="silencioso" tamanho="sm" onClick={() => comecar('toracico')}>
                    {medida.toracico ? 'Refazer o tórax' : 'Medir o tórax'}
                  </Botao>
                  <Botao variante="silencioso" tamanho="sm" onClick={apagarTudo}>
                    Apagar
                  </Botao>
                </>
              )}
              {controlesDeZoom}
            </div>

            {temAlguma && (
              <div className="regua__resultado">
                {ict !== null ? (
                  <>
                    <p className="regua__ict">
                      <span className="rotulo">Índice cardiotorácico</span>
                      <strong>{formatarIct(ict)}</strong>
                    </p>
                    <div className="regua__apoio">
                      {/* A referência fica como lembrete ao lado do número, sem
                          cor e sem veredito: quem lê a chapa é o médico, e um
                          selo verde ou vermelho aqui seria o sistema laudando. */}
                      <p className="regua__referencia">
                        referência usual até {formatarIct(ICT_REFERENCIA)} · arraste as pontas para
                        ajustar
                      </p>
                      {cmCardiaco !== null && cmToracico !== null && (
                        <p className="regua__cm">
                          coração <strong>{formatarCm(cmCardiaco)}</strong> · tórax{' '}
                          <strong>{formatarCm(cmToracico)}</strong>
                          <span className="regua__cm-nota">
                            medida na chapa, com a ampliação do exame
                          </span>
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="regua__referencia">
                    Falta medir {medida.cardiaco ? 'o tórax' : 'o coração'} para sair o índice.
                  </p>
                )}

                {ict !== null && aoEscreverNoLaudo && (
                  <label className="regua__escrever">
                    <input
                      type="checkbox"
                      checked={escrevendo}
                      onChange={(e) => alternarEscrita(e.target.checked)}
                    />
                    <span>
                      Escrever no laudo
                      <span className="regua__escrever-dica">
                        {escrevendo ? 'o laudo acompanha a marca' : 'a medida fica só aqui'}
                      </span>
                    </span>
                  </label>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const mostrarPainel = podeMedir || temAlguma;

  return (
    <div className="regua">
      {mostrarPainel && painel('topo')}

      <div className="regua__janela" data-ampliada={zoom > 1 || undefined}>
        <div className="regua__palco" style={{ width: `${zoom * 100}%` }}>
          <img
            ref={imgRef}
            className="regua__chapa"
            src={src}
            alt={descricao}
            onError={aoFalhar}
            onLoad={() => {
              const el = imgRef.current;
              if (el) setCaixa({ w: el.clientWidth, h: el.clientHeight });
            }}
          />

          {/* A camada aparece também para quem NÃO pode medir, desde que haja
              marca: depois de assinado ninguém mais mede, e foi justamente para
              poder mostrar onde a medida foi tomada que os pontos são
              guardados. Sem marca nenhuma ela não existe. */}
          {(podeMedir || temAlguma) && caixa.w > 0 && (
            <svg
              className="regua__camada"
              data-marcando={marcandoAgora || undefined}
              viewBox={`0 0 ${caixa.w} ${caixa.h}`}
              width={caixa.w}
              height={caixa.h}
              onPointerDown={podeMedir ? aoApontar : undefined}
              onPointerMove={(ev) => {
                if (!marcandoAgora) return;
                setCursor(fracaoDoEvento(ev));
              }}
              onPointerLeave={() => setCursor(null)}
            >
              {/* Guia vertical no ponteiro: alinhar a ponta com a borda é mais
                  fácil contra uma linha do que contra o cursor. */}
              {marcandoAgora && cursor && (
                <line
                  className="regua__guia"
                  x1={cursor.x * caixa.w}
                  y1={0}
                  x2={cursor.x * caixa.w}
                  y2={caixa.h}
                />
              )}

              {marcandoAgora && parcial && cursor && (
                <Previa
                  x1={parcial.x1 * caixa.w}
                  x2={cursor.x * caixa.w}
                  y={parcial.y * caixa.h}
                  qual={marcando}
                />
              )}

              {(['toracico', 'cardiaco'] as const).map((qual) => {
                const c = medida[qual];
                if (!c) return null;
                return (
                  <Marca
                    key={qual}
                    qual={qual}
                    c={c}
                    caixa={caixa}
                    cm={qual === 'cardiaco' ? cmCardiaco : cmToracico}
                    interativa={podeMedir}
                    aoPegar={(ponta) => setArrasto({ qual, ponta })}
                    aoEmpurrar={(ponta, delta) =>
                      aoMudar({ ...medida, [qual]: { ...c, [ponta]: limitar(c[ponta] + delta) } })
                    }
                  />
                );
              })}
            </svg>
          )}
        </div>
      </div>

      {mostrarPainel && painel('rodape')}
    </div>
  );
}

/* ---------------------------------------------------------------- desenho --- */

/** Meia altura das pontas do compasso, em pixels de tela. */
const PONTA = 9;

function Previa({ x1, x2, y, qual }: { x1: number; x2: number; y: number; qual: Qual }) {
  return (
    <g className={`regua__marca regua__marca--${qual}`} data-previa="">
      <line className="regua__halo" x1={x1} y1={y} x2={x2} y2={y} />
      <line className="regua__traco" x1={x1} y1={y} x2={x2} y2={y} />
      <line className="regua__traco" x1={x1} y1={y - PONTA} x2={x1} y2={y + PONTA} />
    </g>
  );
}

function Marca({
  qual,
  c,
  caixa,
  cm,
  interativa,
  aoPegar,
  aoEmpurrar,
}: {
  qual: Qual;
  c: Compasso;
  caixa: { w: number; h: number };
  /** Centímetros desta medida, quando a chapa é calibrada. */
  cm: number | null;
  /** Falso quando a marca é só registro: laudo assinado, ou quem não lauda. */
  interativa: boolean;
  aoPegar: (ponta: 'x1' | 'x2' | 'linha') => void;
  aoEmpurrar: (ponta: 'x1' | 'x2', delta: number) => void;
}) {
  const y = c.y * caixa.h;
  const x1 = c.x1 * caixa.w;
  const x2 = c.x2 * caixa.w;
  const esquerda = Math.min(x1, x2);

  const pegar = (ponta: 'x1' | 'x2' | 'linha') => (ev: React.PointerEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    aoPegar(ponta);
  };

  const teclado = (ponta: 'x1' | 'x2') => (ev: React.KeyboardEvent) => {
    const passo = ev.key === 'ArrowLeft' ? -PASSO_TECLADO : ev.key === 'ArrowRight' ? PASSO_TECLADO : 0;
    if (!passo) return;
    ev.preventDefault();
    aoEmpurrar(ponta, ev.shiftKey ? passo * 5 : passo);
  };

  return (
    <g className={`regua__marca regua__marca--${qual}`}>
      <line className="regua__halo" x1={x1} y1={y} x2={x2} y2={y} />
      <line className="regua__halo" x1={x1} y1={y - PONTA} x2={x1} y2={y + PONTA} />
      <line className="regua__halo" x1={x2} y1={y - PONTA} x2={x2} y2={y + PONTA} />

      <line className="regua__traco" x1={x1} y1={y} x2={x2} y2={y} />
      <line className="regua__traco" x1={x1} y1={y - PONTA} x2={x1} y2={y + PONTA} />
      <line className="regua__traco" x1={x2} y1={y - PONTA} x2={x2} y2={y + PONTA} />

      {/* Começa depois da alça da esquerda, e não em cima dela: o rótulo
          encostado no círculo fica ilegível justo quando o médico está mirando
          a ponta. O centímetro vem junto, que é onde ele é lido de verdade --
          olhando a medida, e não o painel. */}
      <text className="regua__nome" x={esquerda + PONTA + 4} y={y - PONTA - 8}>
        {PASSOS[qual].nome}
        {cm !== null ? ` · ${formatarCm(cm)}` : ''}
      </text>

      {/* Faixa invisível sobre o traço: arrasta a medida inteira para cima e
          para baixo, que é o ajuste mais comum depois de marcar -- achar a
          altura em que o coração é mais largo. */}
      {interativa && (
        <line className="regua__corpo" x1={x1} y1={y} x2={x2} y2={y} onPointerDown={pegar('linha')} />
      )}

      {interativa &&
        (['x1', 'x2'] as const).map((ponta) => (
          <circle
            key={ponta}
            className="regua__alca"
            cx={ponta === 'x1' ? x1 : x2}
            cy={y}
            r={PONTA}
            tabIndex={0}
            role="slider"
            aria-label={`${PASSOS[qual].nome}: ponta ${ponta === 'x1' ? 'esquerda' : 'direita'}`}
            aria-valuenow={Math.round((ponta === 'x1' ? c.x1 : c.x2) * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            onPointerDown={pegar(ponta)}
            onKeyDown={teclado(ponta)}
          />
        ))}
    </g>
  );
}
